import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheck,
  FaMoneyBillWave,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import Header from "../../../components/ManagementHeader";
import { supabase } from "../../../supabaseClient";

const STATUS_OPTIONS = ["Submitted", "Approved", "Rejected", "Paid"];
const DEFAULT_ROBUX_PER_MINUTE = 20;

function calculateMinutes(start, end, breakMinutes) {
  if (!start || !end) return 0;
  const [startHour, startMinute] = start.slice(0, 5).split(":").map(Number);
  const [endHour, endMinute] = end.slice(0, 5).split(":").map(Number);
  let startTotal = startHour * 60 + startMinute;
  let endTotal = endHour * 60 + endMinute;
  if (endTotal < startTotal) endTotal += 1440;
  return Math.max(endTotal - startTotal - Number(breakMinutes || 0), 0);
}

function formatDate(value) {
  if (!value) return "—";
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function settingToBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value && typeof value === "object" && "enabled" in value) {
    return Boolean(value.enabled);
  }
  return value !== false;
}

function managementTimesheetError(error) {
  if (!error) return "Unable to load staff timesheets.";

  if (error.code === "42703" || /week_start|shift_date|start_time|end_time/i.test(error.message || "")) {
    return "The timesheet database needs the latest PGUH upgrade. Run FIX_TIMESHEET_AND_PAGER.sql in Supabase, then refresh this page.";
  }

  if (error.code === "42501" || error.code === "PGRST301") {
    return "Your account does not have permission to review staff timesheets.";
  }

  return error.message || "Unable to load staff timesheets.";
}

function rowRate(entry) {
  if (Number(entry.robux_per_minute) > 0) {
    return Number(entry.robux_per_minute);
  }
  if (Number(entry.total_minutes) > 0 && Number(entry.robux_pay) >= 0) {
    return Number(entry.robux_pay) / Number(entry.total_minutes);
  }
  return DEFAULT_ROBUX_PER_MINUTE;
}

export default function ManagementTimesheet({ session, profile }) {
  const [timesheets, setTimesheets] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [search, setSearch] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [savingSetting, setSavingSetting] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState(null);

  const loadTimesheets = async () => {
    setLoading(true);

    const [settingsResult, timesheetsResult] = await Promise.all([
      supabase
        .from("app_settings")
        .select("value")
        .eq("key", "timesheets_enabled")
        .maybeSingle(),
      supabase
        .from("timesheets")
        .select(
          "id, user_id, staff_name, staff_position, week_start, shift_date, start_time, end_time, break_minutes, notes, total_minutes, robux_per_minute, robux_pay, status, created_at, updated_at",
        )
        .order("week_start", { ascending: false })
        .order("staff_name", { ascending: true })
        .order("shift_date", { ascending: true }),
    ]);

    if (!settingsResult.error && settingsResult.data) {
      setEnabled(settingToBoolean(settingsResult.data.value));
    }

    if (timesheetsResult.error) {
      console.error("Unable to load timesheets:", timesheetsResult.error);
      setMessage({
        type: "error",
        text: managementTimesheetError(timesheetsResult.error),
      });
      setTimesheets([]);
    } else {
      const rows = timesheetsResult.data || [];
      setTimesheets(rows);
      if (rows.length && !selectedStaffId) {
        setSelectedStaffId(rows[0].user_id);
        setSelectedWeek(rows[0].week_start);
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    void loadTimesheets();
    // Load once when the management page is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const staffList = useMemo(() => {
    const staff = new Map();

    timesheets.forEach((entry) => {
      if (!staff.has(entry.user_id)) {
        staff.set(entry.user_id, {
          user_id: entry.user_id,
          staff_name: entry.staff_name || "Unknown staff member",
          staff_position: entry.staff_position || "Staff",
          entries: 0,
          pending: 0,
        });
      }
      const record = staff.get(entry.user_id);
      record.entries += 1;
      if (entry.status === "Submitted") record.pending += 1;
    });

    const query = search.trim().toLowerCase();
    return [...staff.values()]
      .filter((record) =>
        !query
          ? true
          : `${record.staff_name} ${record.staff_position}`
              .toLowerCase()
              .includes(query),
      )
      .sort((a, b) => a.staff_name.localeCompare(b.staff_name));
  }, [search, timesheets]);

  const selectedStaff = useMemo(
    () => staffList.find((staff) => staff.user_id === selectedStaffId),
    [selectedStaffId, staffList],
  );

  const availableWeeks = useMemo(
    () =>
      [...new Set(
        timesheets
          .filter((entry) => entry.user_id === selectedStaffId)
          .map((entry) => entry.week_start)
          .filter(Boolean),
      )].sort((a, b) => new Date(b) - new Date(a)),
    [selectedStaffId, timesheets],
  );

  const selectedEntries = useMemo(
    () =>
      timesheets
        .filter(
          (entry) =>
            entry.user_id === selectedStaffId &&
            entry.week_start === selectedWeek,
        )
        .sort((a, b) => a.shift_date.localeCompare(b.shift_date)),
    [selectedStaffId, selectedWeek, timesheets],
  );

  const totals = useMemo(() => {
    return selectedEntries.reduce(
      (result, entry) => {
        result.minutes += Number(entry.total_minutes || 0);
        result.robux += Number(entry.robux_pay || 0);
        result[entry.status?.toLowerCase() || "submitted"] += 1;
        return result;
      },
      { minutes: 0, robux: 0, submitted: 0, approved: 0, rejected: 0, paid: 0 },
    );
  }, [selectedEntries]);

  const selectStaff = (staff) => {
    const firstWeek = timesheets
      .filter((entry) => entry.user_id === staff.user_id)
      .map((entry) => entry.week_start)
      .filter(Boolean)
      .sort((a, b) => new Date(b) - new Date(a))[0];

    setSelectedStaffId(staff.user_id);
    setSelectedWeek(firstWeek || "");
    setMessage(null);
  };

  const updateLocalEntry = (id, changes) => {
    setTimesheets((current) =>
      current.map((entry) =>
        entry.id === id ? { ...entry, ...changes } : entry,
      ),
    );
  };

  const updateEntryField = (id, field, value) => {
    updateLocalEntry(id, { [field]: value });
  };

  const saveEntry = async (entry) => {
    setSavingId(entry.id);
    setMessage(null);

    const minutes = calculateMinutes(
      entry.start_time,
      entry.end_time,
      entry.break_minutes,
    );
    const rate = rowRate(entry);
    const robux = Math.round(minutes * rate);

    const { error } = await supabase
      .from("timesheets")
      .update({
        start_time: entry.start_time,
        end_time: entry.end_time,
        break_minutes: Number(entry.break_minutes || 0),
        notes: entry.notes || "",
        total_minutes: minutes,
        robux_per_minute: rate,
        robux_pay: robux,
        status: entry.status,
      })
      .eq("id", entry.id);

    setSavingId(null);

    if (error) {
      console.error("Unable to save timesheet entry:", error);
      setMessage({ type: "error", text: "The entry could not be saved." });
      return;
    }

    updateLocalEntry(entry.id, {
      total_minutes: minutes,
      robux_per_minute: rate,
      robux_pay: robux,
    });
    setMessage({ type: "success", text: "Timesheet entry saved." });
  };

  const updateWholeWeekStatus = async (status) => {
    if (!selectedEntries.length) return;
    const ids = selectedEntries.map((entry) => entry.id);
    const { error } = await supabase
      .from("timesheets")
      .update({ status })
      .in("id", ids);

    if (error) {
      setMessage({ type: "error", text: "The week status could not be updated." });
      return;
    }

    setTimesheets((current) =>
      current.map((entry) =>
        ids.includes(entry.id) ? { ...entry, status } : entry,
      ),
    );
    setMessage({ type: "success", text: `Week marked as ${status}.` });
  };

  const toggleTimesheets = async () => {
    const nextValue = !enabled;
    setSavingSetting(true);

    const { error } = await supabase
      .from("app_settings")
      .upsert(
        {
          key: "timesheets_enabled",
          value: nextValue,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "key" },
      );

    setSavingSetting(false);

    if (error) {
      console.error("Unable to update timesheet setting:", error);
      setMessage({ type: "error", text: "The global setting could not be changed." });
      return;
    }

    setEnabled(nextValue);
    setMessage({
      type: "success",
      text: `Timesheet logging is now ${nextValue ? "enabled" : "disabled"}.`,
    });
  };

  return (
    <div className="pguh-page-shell">
      <Header session={session} profile={profile} />

      <main id="main-content" tabIndex="-1" className="pguh-main">
        <div className="nhsuk-width-container">
          <div className="pguh-section-heading">
            <div>
              <span className="pguh-eyebrow">Management payroll</span>
              <h1 className="nhsuk-heading-xl">Timesheet manager</h1>
              <p className="nhsuk-body-l">
                Review submitted time, correct entries and manage approval and payment status.
              </p>
            </div>

            <button
              type="button"
              className={`pguh-timesheet-switch${enabled ? " pguh-timesheet-switch--on" : ""}`}
              onClick={toggleTimesheets}
              disabled={savingSetting}
              aria-pressed={enabled}
            >
              <span aria-hidden="true" />
              {savingSetting
                ? "Updating…"
                : enabled
                  ? "Logging enabled"
                  : "Logging disabled"}
            </button>
          </div>

          {message && (
            <div
              className={`pguh-timesheet-message pguh-timesheet-message--${message.type}`}
              role={message.type === "error" ? "alert" : "status"}
            >
              {message.text}
            </div>
          )}

          <section className="pguh-timesheet-admin-summary" aria-label="Timesheet service status">
            <div><span>Service</span><strong>{enabled ? "Open" : "Closed"}</strong></div>
            <div><span>Staff represented</span><strong>{staffList.length}</strong></div>
            <div><span>Total records</span><strong>{timesheets.length}</strong></div>
            <div><span>Awaiting review</span><strong>{timesheets.filter((entry) => entry.status === "Submitted").length}</strong></div>
          </section>

          {loading ? (
            <p className="nhsuk-body">Loading staff timesheets…</p>
          ) : timesheets.length === 0 ? (
            <div className="pguh-empty-state">
              <h2>No timesheets submitted</h2>
              <p>Staff submissions will appear here.</p>
            </div>
          ) : (
            <div className="pguh-timesheet-manager-layout">
              <aside className="pguh-timesheet-staff-list">
                <div className="pguh-panel__header">
                  <div>
                    <span className="pguh-eyebrow">Directory</span>
                    <h2 className="nhsuk-heading-l">Staff</h2>
                  </div>
                </div>

                <div className="pguh-timesheet-staff-search">
                  <FaSearch aria-hidden="true" />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search staff"
                    aria-label="Search staff timesheets"
                  />
                </div>

                <div className="pguh-timesheet-staff-cards">
                  {staffList.map((staff) => (
                    <button
                      type="button"
                      key={staff.user_id}
                      className={selectedStaffId === staff.user_id ? "is-active" : ""}
                      onClick={() => selectStaff(staff)}
                    >
                      <strong>{staff.staff_name}</strong>
                      <span>{staff.staff_position}</span>
                      <small>
                        {staff.entries} entries
                        {staff.pending ? ` · ${staff.pending} pending` : ""}
                      </small>
                    </button>
                  ))}
                </div>
              </aside>

              <section className="pguh-timesheet-review">
                <div className="pguh-timesheet-review__heading">
                  <div>
                    <span className="pguh-eyebrow">Selected staff</span>
                    <h2 className="nhsuk-heading-l">
                      {selectedStaff?.staff_name || "Select a staff member"}
                    </h2>
                    <p>{selectedStaff?.staff_position}</p>
                  </div>

                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="manager-timesheet-week">Week starting</label>
                    <select
                      id="manager-timesheet-week"
                      className="nhsuk-select"
                      value={selectedWeek}
                      onChange={(event) => setSelectedWeek(event.target.value)}
                    >
                      {availableWeeks.map((week) => (
                        <option value={week} key={week}>{formatDate(week)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="pguh-timesheet-summary pguh-timesheet-summary--management">
                  <div><span>Minutes</span><strong>{totals.minutes}</strong></div>
                  <div><span>Robux</span><strong>{totals.robux.toLocaleString("en-GB")}</strong></div>
                  <div><span>Pending</span><strong>{totals.submitted}</strong></div>
                  <div><span>Approved</span><strong>{totals.approved}</strong></div>
                  <div><span>Rejected</span><strong>{totals.rejected}</strong></div>
                  <div><span>Paid</span><strong>{totals.paid}</strong></div>
                </div>

                <div className="pguh-timesheet-week-actions">
                  <button type="button" className="nhsuk-button" onClick={() => updateWholeWeekStatus("Approved")}>
                    <FaCheck aria-hidden="true" /> Approve week
                  </button>
                  <button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={() => updateWholeWeekStatus("Rejected")}>
                    <FaTimes aria-hidden="true" /> Reject week
                  </button>
                  <button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={() => updateWholeWeekStatus("Paid")}>
                    <FaMoneyBillWave aria-hidden="true" /> Mark week paid
                  </button>
                </div>

                <div className="pguh-timesheet-admin-entries">
                  {selectedEntries.map((entry) => (
                    <article key={entry.id}>
                      <div className="pguh-timesheet-admin-entry__heading">
                        <div>
                          <h3>{formatDate(entry.shift_date)}</h3>
                          <span>{entry.total_minutes || 0} minutes · {Number(entry.robux_pay || 0).toLocaleString("en-GB")} Robux</span>
                        </div>
                        <select
                          value={entry.status || "Submitted"}
                          onChange={(event) => updateEntryField(entry.id, "status", event.target.value)}
                          aria-label={`Status for ${formatDate(entry.shift_date)}`}
                        >
                          {STATUS_OPTIONS.map((status) => <option key={status}>{status}</option>)}
                        </select>
                      </div>

                      <div className="pguh-timesheet-admin-entry__fields">
                        <label>Start<input type="time" value={entry.start_time?.slice(0, 5) || ""} onChange={(event) => updateEntryField(entry.id, "start_time", event.target.value)} /></label>
                        <label>End<input type="time" value={entry.end_time?.slice(0, 5) || ""} onChange={(event) => updateEntryField(entry.id, "end_time", event.target.value)} /></label>
                        <label>Break<input type="number" min="0" value={entry.break_minutes || 0} onChange={(event) => updateEntryField(entry.id, "break_minutes", event.target.value)} /></label>
                        <label>Rate<input type="number" min="0" value={rowRate(entry)} onChange={(event) => updateEntryField(entry.id, "robux_per_minute", event.target.value)} /></label>
                      </div>

                      <label className="pguh-timesheet-admin-entry__notes">
                        Work notes
                        <textarea rows="3" value={entry.notes || ""} onChange={(event) => updateEntryField(entry.id, "notes", event.target.value)} />
                      </label>

                      <button
                        type="button"
                        className="nhsuk-button"
                        onClick={() => saveEntry(entry)}
                        disabled={savingId === entry.id}
                      >
                        {savingId === entry.id ? "Saving…" : "Save entry"}
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
